// What the shell wants said on every page (decision 064): the platform
// refusing this companion (F408), a newer release out (F412), X-Plane
// running an aircraft nobody booked (decision 063), the airframes folder
// not found. Rendered under the host alone, above the page.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { External } from "@/components/External";
import { useHostEvents } from "@/host/events";
import { useHost } from "@/host/host";
import { hostQueries } from "@/host/queries";
import { useReleaseLink } from "@/host/links";
import { companionFile, usePlatform } from "@/host/platform";
import type { AppInfo, PluginBehind, UpdateInfo } from "@/host/types";

export function HostBanners() {
  const host = useHost();
  const info = useQuery({ ...hostQueries.info(host!), enabled: !!host });
  if (!host || !info.data) return null;
  const i = info.data;
  return (
    <>
      {i.compat && !i.compat.companion_ok ? <UpdateRequired version={i.version} min={i.compat.companion_min} ready={i.update_ready ?? null} /> : null}
      {i.compat && i.compat.companion_ok ? <UpdateAvailable version={i.version} latest={i.compat.companion_current ? null : i.compat.companion_latest} plugin={i.plugin_behind} pluginLatest={i.compat.plugin_latest} ready={i.update_ready ?? null} /> : null}
      {i.unleased_run && !i.session ? <UnleasedRun run={i.unleased_run} /> : null}
      {i.airframes_dir_error ? (
        <p className="alert mb-3" data-testid="folder-missing">
          {i.airframes_dir_error}{" "}
          <Link to="/pc/settings" className="underline">
            Open the settings
          </Link>
        </p>
      ) : null}
    </>
  );
}

/** The platform needs a newer companion (F408): said first, with the way out; the platform refuses this one anyway. */
function UpdateRequired({ version, min, ready }: { version: string; min: string | null; ready: string | null }) {
  const host = useHost()!;
  const queryClient = useQueryClient();
  const again = useMutation({
    mutationFn: () => host.compatRefresh(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["host", "info"] }),
  });
  return (
    <div className="alert mb-3" role="alert" data-testid="update-required">
      <p className="font-semibold">Update required</p>
      <p className="text-sm">
        This companion is {version}; the platform needs {min ?? "a newer one"}. The platform refuses it until it is updated: check, download, install (X-Plane closed), then check again.
      </p>
      <p className="mt-2 flex flex-wrap items-center gap-2">
        <UpdateSteps ready={ready} />
        <button type="button" className="btn btn-sm" disabled={again.isPending} onClick={() => again.mutate()}>
          Check again
        </button>
      </p>
    </div>
  );
}

/** A newer release is out (F412, decision 054): said plainly, nothing refused; the updater cannot read the private repository, so the pilot installs by hand, X-Plane closed. */
function UpdateAvailable({ version, latest, plugin, pluginLatest, ready }: { version: string; latest: string | null; plugin: PluginBehind | null; pluginLatest: string | null; ready: string | null }) {
  // `latest` is null when this companion is current (the Rust side compared the release numbers).
  const companion = latest !== null;
  if (!companion && !plugin) return null;
  return (
    <div className="notice mb-3" role="status" data-testid="update-available">
      <p className="font-semibold">Update available</p>
      <p className="text-sm">
        {companion ? `The companion ${latest} is out; this one is ${version}. ` : ""}
        {plugin ? `The plugin in X-Plane is ${plugin.found}; ${pluginLatest ?? plugin.latest} is out. ` : ""}
        Update before booking: check, download, then install with X-Plane closed (the companion closes and the installer takes over); the plugin is fetched from the platform and put into X-Plane at the next start with X-Plane closed. A booking or a watch waits until this PC is at the latest.
      </p>
      <p className="mt-2 flex flex-wrap items-center gap-2">
        <UpdateSteps ready={ready} />
      </p>
    </div>
  );
}

/**
 * The updater in three clicks (F208, decision 066; F489, decision 102 item 6):
 * "Check for updates" asks the platform's store, "Download" fetches and
 * verifies the release and keeps it, "Install" closes the companion and
 * starts the installer. Nothing runs by itself; a download kept from
 * earlier in this run is offered to install right away. A download link by
 * hand beside it.
 */
export function UpdateSteps({ ready = null }: { ready?: string | null }) {
  const host = useHost()!;
  const queryClient = useQueryClient();
  const events = useHostEvents();
  const download = useReleaseLink(companionFile(usePlatform()));
  const [found, setFound] = useState<UpdateInfo | null>(null);
  const [downloaded, setDownloaded] = useState<string | null>(ready);
  const [note, setNote] = useState<string | null>(ready ? `${ready} is downloaded and ready to install` : null);
  // A download kept by the shell (from this or another copy of the steps on the page) is offered here too.
  useEffect(() => {
    if (ready && ready !== downloaded) {
      setDownloaded(ready);
      setNote(`${ready} is downloaded and ready to install`);
    }
  }, [ready, downloaded]);
  const check = useMutation({
    mutationFn: () => host.updateCheck(),
    onSuccess: (result) => {
      if (!result.found) {
        setFound(null);
        setNote(`${result.current_version} is the latest on the ${result.channel} channel`);
        return;
      }
      // The release's body (decision 119): a changelog in lists is shown below; a one-line body stays beside the version.
      const lines = changelog(result.found.notes);
      setFound(result.found);
      setNote(lines.length > 1 ? `${result.found.version} is out; what is new is below` : `${result.found.version} is out${lines[0] ? ` (${lines[0]})` : ""}`);
    },
    onError: (e) => setNote(String(e)),
  });
  const fetch = useMutation({
    mutationFn: () => host.updateDownload(),
    onSuccess: (info) => {
      setDownloaded(info.version);
      setNote(`${info.version} is downloaded and ready to install`);
      queryClient.invalidateQueries({ queryKey: ["host", "info"] });
    },
    onError: (e) => setNote(String(e)),
  });
  const install = useMutation({
    mutationFn: () => host.updateInstall(),
    onSuccess: (version) => setNote(`${version} is installing; the companion closes and the installer takes over`),
    onError: (e) => setNote(String(e)),
  });
  const busy = check.isPending || fetch.isPending || install.isPending;
  const progress = events.transfers["companion"];
  return (
    <>
      {downloaded ? (
        <button type="button" className="btn btn-sm btn-primary" disabled={busy} onClick={() => install.mutate()} data-testid="install-update">
          Install {downloaded}
        </button>
      ) : found ? (
        <button type="button" className="btn btn-sm btn-primary" disabled={busy} onClick={() => fetch.mutate()} data-testid="download-update">
          Download {found.version}
        </button>
      ) : (
        <button type="button" className="btn btn-sm" disabled={busy} onClick={() => check.mutate()} data-testid="check-update">
          Check for updates
        </button>
      )}
      <External href={download} className="btn btn-sm">
        Download by hand
      </External>
      {fetch.isPending && progress && progress.total > 0 ? <span className="muted text-xs tnum">{Math.round((progress.done * 100) / progress.total)}%</span> : null}
      {note ? <span className={`text-xs ${check.isError || fetch.isError || install.isError ? "text-bad" : "muted"}`} data-testid="update-note">{note}</span> : null}
      {found && !downloaded && changelog(found.notes).length > 1 ? (
        <div className="basis-full text-sm" data-testid="update-notes">
          {changelog(found.notes).map((line, i) =>
            line.startsWith("## ") ? (
              <p key={i} className="mt-2 font-semibold first:mt-0">
                {line.slice(3)}
              </p>
            ) : (
              <p key={i} className={line.startsWith("- ") ? "pl-3" : "muted"}>
                {line.startsWith("- ") ? `• ${line.slice(2)}` : line}
              </p>
            ),
          )}
        </div>
      ) : null}
    </>
  );
}

/** The lines of a release's body, blank ones dropped: the lists of the pilots' note, or one plain line. */
export function changelog(notes: string | null | undefined): string[] {
  return (notes ?? "")
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l !== "");
}

/** X-Plane runs a shared airframe nobody booked (decisions 063 and 124): said plainly, with the one way to have a flight count. */
function UnleasedRun({ run }: { run: NonNullable<AppInfo["unleased_run"]> }) {
  return (
    <div className="alert mb-3" role="alert" data-testid="unleased-run">
      <p className="font-semibold">You are flying {run.reg} without a booking</p>
      <p className="text-sm">X-Plane has {run.reg} loaded, but you did not book it. Flown like this, the flight stays on your PC and ShariFly records nothing.</p>
      <p className="mt-1 text-sm">
        After a booked flight, closing X-Plane is what makes ShariFly upload the aircraft with its hours, its fuel and its wear, so that the next pilot finds it as you left it. None of that
        happens now.
      </p>
      <p className="mt-1 text-sm">To have a flight count, close X-Plane, book {run.reg} in the app and fly it again. Nothing from this session carries over.</p>
    </div>
  );
}
